import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, SafeAreaView, StatusBar,
  TextInput, ActivityIndicator, Alert, Modal, Animated, KeyboardAvoidingView, Platform, Share,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiFetch } from '../../utils/apiClient';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
import useProfile from '../../hooks/useProfile';

let DocumentPicker;
try { DocumentPicker = require('expo-document-picker'); } catch { DocumentPicker = null; }

let LinearGradient;
try { LinearGradient = require('expo-linear-gradient').LinearGradient; }
catch { LinearGradient = ({colors,style,children,...p})=><View style={[style,{backgroundColor:colors?.[0]||'#0e4e96'}]} {...p}>{children}</View>; }

const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const PROF_KEYS = { PROFILE:'prof_user_profile' };
const APPS_KEY  = 'grad_applications';

const T = {primary:'#0e4e96',primaryLight:'#1e7be2',primaryBg:'#EFF6FF',success:'#10b981',successBg:'#D1FAE5',warning:'#f59e0b',danger:'#ef4444',text:'#1F2937',textSec:'#64748B',textMut:'#94A3B8',border:'#E2E8F0',borderLight:'#F1F5F9',card:'#FFF',bg:'#F8FAFC',white:'#FFF'};

const STEPS = [{id:1,title:'Personal',icon:'person-outline'},{id:2,title:'Academic',icon:'school-outline'},{id:3,title:'Motivation',icon:'document-text-outline'},{id:4,title:'Documents',icon:'cloud-upload-outline'}];
const SKILL_OPTIONS = ['Leadership','Communication','Teamwork','Problem Solving','Analytical','Project Management','Research','Data Analysis','Programming','Public Speaking','Critical Thinking','Time Management'];
const ROTATION_OPTIONS = ['Engineering','Technology','Finance','Operations','Marketing','Strategy','HR','R&D','Supply Chain','Sales'];

export default function GraduateProgramApplicationScreen({ navigation, route }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const programId = route.params?.programId || route.params?.id;
  const program = route.params?.program || route.params?.programData || null;

  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    firstName:'',lastName:'',email:'',phone:'',location:'',linkedinUrl:'',
    university:'',degree:'',fieldOfStudy:'',graduationYear:'',gpa:'',honors:'',
    skills:[],preferredRotations:[],
    motivationStatement:'',whyThisProgram:'',careerGoals:'',additionalInfo:'',
    workExperience:'',extracurriculars:'',
    documents:[],agreeToTerms:false,allowContact:true,
  });

  const scrollRef = useRef(null);
  const fadeAnim = useRef(new Animated.Value(0)).current;

  // ── Init: pre-fill from profile ──
  useEffect(() => {
    prefillFromProfile();
    Animated.timing(fadeAnim,{toValue:1,duration:600,useNativeDriver:true}).start();
  }, []);

  const prefillFromProfile = async () => {
    try {
      const raw = await AsyncStorage.getItem(PROF_KEYS.PROFILE)||await AsyncStorage.getItem('userProfile');
      if (!raw) return;
      const prof = JSON.parse(raw);
      const p = prof.personal || {};
      const nameParts = (p.fullName||'').split(' ');
      setForm(prev => ({
        ...prev,
        firstName: nameParts[0] || prev.firstName,
        lastName: nameParts.slice(1).join(' ') || prev.lastName,
        email: p.email || prev.email,
        phone: p.phone || prev.phone,
        location: p.location || prev.location,
        linkedinUrl: p.linkedin || prev.linkedinUrl,
        university: (prof.education||[])[0]?.institution || prev.university,
        degree: (prof.education||[])[0]?.degree || prev.degree,
        fieldOfStudy: (prof.education||[])[0]?.field_of_study || prev.fieldOfStudy,
        gpa: (prof.education||[])[0]?.grade || prev.gpa,
        skills: (prof.skills||[]).slice(0,6).map(s=>s.name||s.skill_name||'').filter(Boolean),
      }));
    } catch {}
  };

  const set = useCallback((field, value) => {
    setForm(prev => ({...prev,[field]:value}));
    setErrors(prev => ({...prev,[field]:null}));
  }, []);

  const toggleChip = (field, value) => {
    setForm(prev => {
      const arr = prev[field] || [];
      return {...prev, [field]: arr.includes(value) ? arr.filter(v=>v!==value) : [...arr, value]};
    });
  };

  // ── Validation ──
  const validate = (s) => {
    const e = {};
    if (s===1) {
      if (!form.firstName.trim()) e.firstName='Required';
      if (!form.lastName.trim()) e.lastName='Required';
      if (!form.email.trim()) e.email='Required';
      else if (!/\S+@\S+\.\S+/.test(form.email)) e.email='Invalid email';
      if (!form.phone.trim()) e.phone='Required';
    }
    if (s===2) {
      if (!form.university.trim()) e.university='Required';
      if (!form.degree.trim()) e.degree='Required';
      if (!form.graduationYear.trim()) e.graduationYear='Required';
      else if (!/^\d{4}$/.test(form.graduationYear)) e.graduationYear='Enter a valid year';
      if (form.skills.length===0) e.skills='Select at least one skill';
    }
    if (s===3) {
      if (!form.motivationStatement.trim()) e.motivationStatement='Required';
      if (form.motivationStatement.trim().length<50) e.motivationStatement='At least 50 characters';
      if (!form.whyThisProgram.trim()) e.whyThisProgram='Required';
      if (form.preferredRotations.length===0) e.preferredRotations='Select at least one rotation';
    }
    if (s===4) {
      if (!form.agreeToTerms) e.agreeToTerms='You must agree to terms';
    }
    setErrors(e);
    return Object.keys(e).length===0;
  };

  const handleNext = () => {
    if (!validate(step)) { Alert.alert('Missing Fields','Please fill in all required fields.'); return; }
    if (step<4) { setStep(step+1); scrollRef.current?.scrollTo({y:0,animated:true}); }
    else setShowPreview(true);
  };
  const handlePrev = () => { if(step>1) { setStep(step-1); scrollRef.current?.scrollTo({y:0,animated:true}); } };

  // ── Document Picker ──
  const pickDoc = async (type) => {
    if (!DocumentPicker) { Alert.alert('Install','Run: npx expo install expo-document-picker'); return; }
    try {
      const result = await DocumentPicker.getDocumentAsync({type:['application/pdf','application/msword','application/vnd.openxmlformats-officedocument.wordprocessingml.document'],copyToCacheDirectory:true});
      if (!result.canceled && result.assets?.[0]) {
        const doc = result.assets[0];
        set('documents', [...form.documents, {id:`doc_${Date.now()}`,name:doc.name,uri:doc.uri,type:type,size:doc.size,mimeType:doc.mimeType}]);
      }
    } catch { Alert.alert('Error','Could not pick document.'); }
  };
  const removeDoc = (id) => set('documents', form.documents.filter(d=>d.id!==id));

  // ── AI Generate ──
  const generateMotivation = async () => {
    setAiGenerating(true);
    try {
      const prompt = `Write a motivation statement for a graduate applying to the ${program?.title||'graduate program'} at ${program?.company||'the company'}. The applicant studied ${form.degree||'a degree'} at ${form.university||'university'}. Skills: ${form.skills.join(', ')||'various'}. Keep it professional, enthusiastic, and under 200 words.`;
      const r = await apiFetch('/api/ai/career-advice',{method:'POST',body:JSON.stringify({prompt})});
      if (r?.advice||r?.text) { set('motivationStatement', r.advice||r.text); Alert.alert('Generated','Motivation statement generated. Feel free to edit.'); }
      else Alert.alert('Unavailable','AI service unavailable. Please write manually.');
    } catch { Alert.alert('Error','Could not generate statement.'); }
    setAiGenerating(false);
  };

  // ── Submit ──
  const submitApplication = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        program_id: programId, program_title: program?.title||'', company: program?.company||'',
        user_id: null, // will be set server-side if authenticated
        status: 'Submitted', progress: 10,
        cover_letter: form.motivationStatement,
        why_interested: form.whyThisProgram,
        relevant_experience: form.workExperience,
        availability: form.graduationYear,
        additional_info: `Skills: ${form.skills.join(', ')}\nRotations: ${form.preferredRotations.join(', ')}\nGPA: ${form.gpa}\nCareer Goals: ${form.careerGoals}\nExtracurriculars: ${form.extracurriculars}`,
        applied_date: new Date().toISOString(),
      };
      await apiFetch('/api/data/internship_applications',{method:'POST',body:JSON.stringify(payload)});

      // Save locally
      const raw = await AsyncStorage.getItem(APPS_KEY);
      let apps = raw ? JSON.parse(raw) : [];
      apps.push({id:`gapp_${Date.now()}`, programId, programTitle:program?.title, company:program?.company, status:'Submitted', appliedDate:new Date().toISOString(), formData:form});
      await AsyncStorage.setItem(APPS_KEY, JSON.stringify(apps));

      setShowPreview(false);
      setShowSuccess(true);
    } catch { Alert.alert('Error','Could not submit. Please try again.'); }
    setIsSubmitting(false);
  };

  // ── Field Component ──
  const Field = ({label,field,placeholder,multiline,lines,keyboardType,autoCapitalize,required,maxLength}) => (
    <View style={{marginBottom:16}}>
      <Text style={st.fieldLabel}>{label}{required&&<Text style={{color:T.danger}}> *</Text>}</Text>
      <TextInput style={[st.input,multiline&&{height:Math.max(80,(lines||3)*20),textAlignVertical:'top'},errors[field]&&{borderColor:T.danger}]}
        value={form[field]} onChangeText={v=>set(field,v)} placeholder={placeholder} placeholderTextColor={T.textMut}
        multiline={multiline} numberOfLines={lines} keyboardType={keyboardType} autoCapitalize={autoCapitalize||'sentences'} maxLength={maxLength}/>
      {errors[field]&&<Text style={st.errT}>{errors[field]}</Text>}
      {maxLength&&multiline&&<Text style={{fontSize:10,color:T.textMut,textAlign:'right',marginTop:2}}>{(form[field]||'').length}/{maxLength}</Text>}
    </View>
  );

  // ── Step Renders ──
  const renderStep1 = () => (
    <View style={st.stepContent}>
      <Text style={st.stepTitle}>Personal Information</Text>
      <Text style={st.stepDesc}>Pre-filled from your profile. Verify and update.</Text>
      <View style={{flexDirection:'row',gap:10}}>
        <View style={{flex:1}}><Field label="First Name" field="firstName" placeholder="John" required/></View>
        <View style={{flex:1}}><Field label="Last Name" field="lastName" placeholder="Smith" required/></View>
      </View>
      <Field label="Email" field="email" placeholder="john@university.edu" keyboardType="email-address" autoCapitalize="none" required/>
      <Field label="Phone" field="phone" placeholder="+44 7700 900000" keyboardType="phone-pad" required/>
      <Field label="Location" field="location" placeholder="London, UK"/>
      <Field label="LinkedIn" field="linkedinUrl" placeholder="https://linkedin.com/in/..." autoCapitalize="none"/>
    </View>
  );

  const renderStep2 = () => (
    <View style={st.stepContent}>
      <Text style={st.stepTitle}>Academic Background</Text>
      <Text style={st.stepDesc}>Tell us about your education and skills.</Text>
      <Field label="University" field="university" placeholder="University of Cambridge" required/>
      <Field label="Degree" field="degree" placeholder="BSc Computer Science" required/>
      <Field label="Field of Study" field="fieldOfStudy" placeholder="Computer Science"/>
      <View style={{flexDirection:'row',gap:10}}>
        <View style={{flex:1}}><Field label="Graduation Year" field="graduationYear" placeholder="2026" keyboardType="numeric" required/></View>
        <View style={{flex:1}}><Field label="GPA / Grade" field="gpa" placeholder="3.8 / First"/></View>
      </View>
      <Field label="Honours / Awards" field="honors" placeholder="Dean's List, Scholarship..."/>
      <Text style={st.fieldLabel}>Key Skills <Text style={{color:T.danger}}>*</Text></Text>
      {errors.skills&&<Text style={st.errT}>{errors.skills}</Text>}
      <View style={{flexDirection:'row',flexWrap:'wrap',gap:8,marginBottom:16}}>
        {SKILL_OPTIONS.map(sk=>(
          <TouchableOpacity key={sk} style={[st.chip,form.skills.includes(sk)&&st.chipActive]} onPress={()=>toggleChip('skills',sk)}>
            <Text style={[st.chipT,form.skills.includes(sk)&&{color:T.white}]}>{sk}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  const renderStep3 = () => (
    <View style={st.stepContent}>
      <Text style={st.stepTitle}>Motivation & Goals</Text>
      <Text style={st.stepDesc}>Share why you're the right candidate.</Text>

      <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
        <Text style={st.fieldLabel}>Motivation Statement <Text style={{color:T.danger}}>*</Text></Text>
        <TouchableOpacity style={st.aiBtn} onPress={generateMotivation} disabled={aiGenerating}>
          {aiGenerating?<ActivityIndicator size={12} color="#7C3AED"/>:<MaterialCommunityIcons name="robot" size={14} color="#7C3AED"/>}
          <Text style={st.aiBtnT}>{aiGenerating?'Generating...':'AI Generate'}</Text>
        </TouchableOpacity>
      </View>
      <TextInput style={[st.input,{height:140,textAlignVertical:'top'},errors.motivationStatement&&{borderColor:T.danger}]}
        value={form.motivationStatement} onChangeText={v=>set('motivationStatement',v)}
        placeholder="Why are you passionate about this program..." placeholderTextColor={T.textMut} multiline maxLength={2000}/>
      {errors.motivationStatement&&<Text style={st.errT}>{errors.motivationStatement}</Text>}

      <Field label="Why this specific program?" field="whyThisProgram" placeholder={`What attracts you to ${program?.company||'this company'}...`} multiline lines={4} required maxLength={1000}/>
      <Field label="Career Goals" field="careerGoals" placeholder="Where do you see yourself in 5 years..." multiline lines={3} maxLength={500}/>
      <Field label="Work Experience" field="workExperience" placeholder="Internships, part-time jobs, volunteering..." multiline lines={3}/>
      <Field label="Extracurricular Activities" field="extracurriculars" placeholder="Societies, sports, competitions..." multiline lines={2}/>

      <Text style={st.fieldLabel}>Preferred Rotations <Text style={{color:T.danger}}>*</Text></Text>
      {errors.preferredRotations&&<Text style={st.errT}>{errors.preferredRotations}</Text>}
      <View style={{flexDirection:'row',flexWrap:'wrap',gap:8,marginBottom:16}}>
        {ROTATION_OPTIONS.map(ro=>(
          <TouchableOpacity key={ro} style={[st.chip,form.preferredRotations.includes(ro)&&st.chipActive]} onPress={()=>toggleChip('preferredRotations',ro)}>
            <Text style={[st.chipT,form.preferredRotations.includes(ro)&&{color:T.white}]}>{ro}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  const renderStep4 = () => (
    <View style={st.stepContent}>
      <Text style={st.stepTitle}>Documents & Review</Text>
      <Text style={st.stepDesc}>Upload your documents and review before submitting.</Text>

      {/* Upload buttons */}
      {[{type:'resume',label:'CV / Resume',icon:'document-text'},{type:'coverLetter',label:'Cover Letter',icon:'mail'},{type:'transcript',label:'Academic Transcript',icon:'school'},{type:'other',label:'Other Document',icon:'attach'}].map(({type,label,icon})=>(
        <TouchableOpacity key={type} style={st.uploadBtn} onPress={()=>pickDoc(type)}>
          <Ionicons name={icon} size={20} color={T.primary}/>
          <View style={{flex:1,marginLeft:10}}><Text style={{fontSize:13,fontWeight:'600',color:T.text}}>Upload {label}</Text>
            {form.documents.find(d=>d.type===type)?<Text style={{fontSize:11,color:T.success,marginTop:1}}>✓ {form.documents.find(d=>d.type===type).name}</Text>:<Text style={{fontSize:11,color:T.textMut,marginTop:1}}>PDF or Word</Text>}
          </View>
          {form.documents.find(d=>d.type===type)
            ?<TouchableOpacity onPress={()=>removeDoc(form.documents.find(d=>d.type===type).id)}><Ionicons name="trash-outline" size={18} color={T.danger}/></TouchableOpacity>
            :<Ionicons name="add-circle-outline" size={20} color={T.primary}/>}
        </TouchableOpacity>
      ))}

      {/* Checkboxes */}
      <View style={{marginTop:20}}>
        {[{f:'agreeToTerms',l:'I agree to the terms and conditions and privacy policy *',req:true},{f:'allowContact',l:'Allow the company to contact me about future opportunities'}].map(({f,l,req})=>(
          <TouchableOpacity key={f} style={{flexDirection:'row',alignItems:'flex-start',gap:10,marginBottom:14}} onPress={()=>set(f,!form[f])}>
            <Ionicons name={form[f]?'checkbox':'square-outline'} size={22} color={form[f]?T.primary:T.textMut}/>
            <Text style={[{flex:1,fontSize:13,color:T.textSec,lineHeight:18},req&&errors[f]&&{color:T.danger}]}>{l}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  // ── Preview Modal ──
  const PRow = ({l,v}) => v?<Text style={{fontSize:13,color:'#475569',marginBottom:4}}><Text style={{fontWeight:'600',color:T.text}}>{l}: </Text>{Array.isArray(v)?v.join(', '):v}</Text>:null;

  const renderPreview = () => (
    <Modal visible={showPreview} animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowPreview(false)}>
      <SafeAreaView style={{flex:1,backgroundColor:T.card}}>
        <View style={st.modalHdr}><TouchableOpacity onPress={()=>setShowPreview(false)}><Ionicons name="close" size={24} color={T.text}/></TouchableOpacity><Text style={{fontSize:17,fontWeight:'700',color:T.text}}>Review Application</Text><View style={{width:24}}/></View>
        <ScrollView style={{flex:1,padding:20}}>
          <View style={{backgroundColor:T.borderLight,borderRadius:12,padding:14,marginBottom:16}}><Text style={{fontSize:16,fontWeight:'700',color:T.text}}>{program?.title||'Graduate Program'}</Text><Text style={{fontSize:13,color:T.primary,marginTop:2}}>{program?.company||''}</Text></View>
          <Text style={{fontSize:15,fontWeight:'700',color:T.text,marginBottom:8}}>Personal</Text>
          <PRow l="Name" v={`${form.firstName} ${form.lastName}`}/><PRow l="Email" v={form.email}/><PRow l="Phone" v={form.phone}/><PRow l="LinkedIn" v={form.linkedinUrl}/>
          <Text style={{fontSize:15,fontWeight:'700',color:T.text,marginTop:14,marginBottom:8}}>Academic</Text>
          <PRow l="University" v={form.university}/><PRow l="Degree" v={`${form.degree} — ${form.fieldOfStudy}`}/><PRow l="Graduation" v={form.graduationYear}/><PRow l="GPA" v={form.gpa}/><PRow l="Skills" v={form.skills}/>
          <Text style={{fontSize:15,fontWeight:'700',color:T.text,marginTop:14,marginBottom:8}}>Motivation</Text>
          <Text style={{fontSize:13,color:'#475569',lineHeight:20,marginBottom:8}}>{form.motivationStatement}</Text>
          <PRow l="Why this program" v={form.whyThisProgram}/><PRow l="Rotations" v={form.preferredRotations}/>
          {form.documents.length>0&&<><Text style={{fontSize:15,fontWeight:'700',color:T.text,marginTop:14,marginBottom:8}}>Documents ({form.documents.length})</Text>{form.documents.map(d=><Text key={d.id} style={{fontSize:12,color:T.textSec}}>• {d.name} ({d.type})</Text>)}</>}
        </ScrollView>
        <View style={{padding:16,borderTopWidth:1,borderTopColor:T.border,gap:10}}>
          <TouchableOpacity style={st.submitBtn} onPress={submitApplication} disabled={isSubmitting}>
            {isSubmitting?<ActivityIndicator color={T.white}/>:<><Text style={st.submitBtnT}>Submit Application</Text><Ionicons name="send" size={18} color={T.white}/></>}
          </TouchableOpacity>
          <TouchableOpacity style={st.editBtn} onPress={()=>setShowPreview(false)}><Text style={st.editBtnT}>Edit Application</Text></TouchableOpacity>
        </View>
      </SafeAreaView>
    </Modal>
  );

  // ── Success Modal ──
  const renderSuccess = () => (
    <Modal visible={showSuccess} animationType="fade" transparent>
      <View style={{flex:1,backgroundColor:'rgba(0,0,0,0.5)',justifyContent:'center',alignItems:'center'}}>
        <View style={{backgroundColor:T.card,borderRadius:20,padding:32,alignItems:'center',marginHorizontal:30}}>
          <View style={{width:72,height:72,borderRadius:36,backgroundColor:T.successBg,justifyContent:'center',alignItems:'center',marginBottom:16}}><Ionicons name="checkmark-circle" size={48} color={T.success}/></View>
          <Text style={{fontSize:22,fontWeight:'800',color:T.text,textAlign:'center',marginBottom:8}}>Application Submitted!</Text>
          <Text style={{fontSize:14,color:T.textSec,textAlign:'center',lineHeight:20,marginBottom:24}}>Your application for <Text style={{fontWeight:'700'}}>{program?.title}</Text> at <Text style={{fontWeight:'700'}}>{program?.company}</Text> has been submitted.</Text>
          <TouchableOpacity style={[st.submitBtn,{marginBottom:10,width:'100%'}]} onPress={()=>{setShowSuccess(false);navigation.navigate('GraduatePrograms');}}>
            <Text style={st.submitBtnT}>View Programs</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[st.editBtn,{width:'100%'}]} onPress={async()=>{try{await Share.share({message:`I just applied for ${program?.title} at ${program?.company}! 🎓`})}catch{}}}>
            <Ionicons name="share-social-outline" size={16} color={T.primary}/><Text style={[st.editBtnT,{marginLeft:6}]}>Share</Text>
          </TouchableOpacity>
          <TouchableOpacity style={{marginTop:10}} onPress={()=>{setShowSuccess(false);navigation.goBack();}}><Text style={{fontSize:14,color:T.textSec}}>Close</Text></TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  // ── MAIN ──
  return (
    <SafeAreaView style={{flex:1,backgroundColor:T.primary}}>
      <StatusBar barStyle="light-content" backgroundColor={T.primary}/>
      <LinearGradient colors={[T.primary,T.primaryLight]} style={st.header}>
        <TouchableOpacity style={st.hdrBtn} onPress={()=>navigation.goBack()}><Ionicons name="arrow-back" size={22} color={T.white}/></TouchableOpacity>
        <View style={{flex:1,marginLeft:10}}><Text style={st.hdrTitle}>Apply for Program</Text><Text style={st.hdrSub} numberOfLines={1}>{program?.title||'Graduate Program'} — {program?.company||''}</Text></View>
      </LinearGradient>

      <Animated.View style={{flex:1,backgroundColor:T.bg,opacity:fadeAnim}}>
        {/* Step Indicator */}
        <View style={st.stepBar}>
          {STEPS.map((s,i)=>(<React.Fragment key={s.id}>
            <TouchableOpacity style={st.stepDot} onPress={()=>{if(s.id<step)setStep(s.id);}}>
              <View style={[st.stepCircle,step>=s.id&&st.stepCircleActive,step===s.id&&st.stepCircleCurrent]}>
                {step>s.id?<Ionicons name="checkmark" size={14} color={T.white}/>:<Text style={[st.stepNum,step>=s.id&&{color:T.white}]}>{s.id}</Text>}
              </View>
              <Text style={[st.stepLabel,step>=s.id&&{color:T.primary,fontWeight:'700'}]}>{s.title}</Text>
            </TouchableOpacity>
            {i<3&&<View style={[st.stepLine,step>s.id&&st.stepLineActive]}/>}
          </React.Fragment>))}
        </View>

        <KeyboardAvoidingView style={{flex:1}} behavior={Platform.OS==='ios'?'padding':undefined}>
          <ScrollView ref={scrollRef} style={{flex:1}} contentContainerStyle={{paddingBottom:20}} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
            {step===1&&renderStep1()}
            {step===2&&renderStep2()}
            {step===3&&renderStep3()}
            {step===4&&renderStep4()}
          </ScrollView>
        </KeyboardAvoidingView>

        {/* Nav */}
        <View style={st.navBar}>
          {step>1?<TouchableOpacity style={st.prevBtn} onPress={handlePrev}><Ionicons name="chevron-back" size={18} color={T.primary}/><Text style={st.prevBtnT}>Back</Text></TouchableOpacity>:<View style={{width:80}}/>}
          <Text style={{fontSize:12,fontWeight:'600',color:T.textMut}}>Step {step}/4</Text>
          <TouchableOpacity style={st.nextBtn} onPress={handleNext}><Text style={st.nextBtnT}>{step===4?'Review':'Next'}</Text><Ionicons name="chevron-forward" size={18} color={T.white}/></TouchableOpacity>
        </View>
      </Animated.View>

      {renderPreview()}
      {renderSuccess()}
    </SafeAreaView>
  );
}

const st = StyleSheet.create({
  header:{flexDirection:'row',alignItems:'center',paddingHorizontal:16,paddingVertical:14},
  hdrBtn:{width:38,height:38,borderRadius:12,backgroundColor:'rgba(255,255,255,0.18)',justifyContent:'center',alignItems:'center'},
  hdrTitle:{fontSize:18,fontWeight:'700',color:'#FFF'},
  hdrSub:{fontSize:12,color:'rgba(255,255,255,0.75)',marginTop:1},
  stepBar:{flexDirection:'row',alignItems:'flex-start',justifyContent:'center',paddingVertical:16,paddingHorizontal:12,backgroundColor:T.card,borderBottomWidth:1,borderBottomColor:T.border},
  stepDot:{alignItems:'center',width:60},
  stepCircle:{width:30,height:30,borderRadius:15,backgroundColor:T.border,justifyContent:'center',alignItems:'center'},
  stepCircleActive:{backgroundColor:T.primary},
  stepCircleCurrent:{backgroundColor:T.primary,borderWidth:3,borderColor:'rgba(14,78,150,0.25)'},
  stepNum:{fontSize:12,fontWeight:'700',color:T.textMut},
  stepLabel:{fontSize:9,color:T.textMut,fontWeight:'500',marginTop:4,textAlign:'center'},
  stepLine:{width:20,height:2,backgroundColor:T.border,marginTop:14,marginHorizontal:2},
  stepLineActive:{backgroundColor:T.primary},
  stepContent:{padding:20,backgroundColor:T.card,marginBottom:1},
  stepTitle:{fontSize:22,fontWeight:'800',color:T.text,marginBottom:4},
  stepDesc:{fontSize:13,color:T.textSec,marginBottom:20},
  fieldLabel:{fontSize:13,fontWeight:'700',color:T.text,marginBottom:6},
  input:{borderWidth:1,borderColor:T.border,borderRadius:12,paddingHorizontal:14,paddingVertical:12,fontSize:14,color:T.text,backgroundColor:T.bg},
  errT:{fontSize:11,color:T.danger,fontWeight:'500',marginTop:3},
  chip:{paddingHorizontal:14,paddingVertical:8,borderRadius:20,borderWidth:1,borderColor:T.border,backgroundColor:T.bg},
  chipActive:{backgroundColor:T.primary,borderColor:T.primary},
  chipT:{fontSize:12,fontWeight:'600',color:T.textSec},
  aiBtn:{flexDirection:'row',alignItems:'center',gap:4,backgroundColor:'#F5F3FF',borderRadius:8,paddingHorizontal:10,paddingVertical:6,borderWidth:1,borderColor:'#DDD6FE'},
  aiBtnT:{fontSize:11,fontWeight:'700',color:'#7C3AED'},
  uploadBtn:{flexDirection:'row',alignItems:'center',padding:14,backgroundColor:T.bg,borderRadius:12,marginBottom:10,borderWidth:1,borderColor:T.border},
  navBar:{flexDirection:'row',alignItems:'center',justifyContent:'space-between',paddingHorizontal:16,paddingVertical:12,backgroundColor:T.card,borderTopWidth:1,borderTopColor:T.border},
  prevBtn:{flexDirection:'row',alignItems:'center',paddingHorizontal:14,paddingVertical:10,borderRadius:10,borderWidth:1,borderColor:T.primary},
  prevBtnT:{fontSize:14,fontWeight:'600',color:T.primary,marginLeft:2},
  nextBtn:{flexDirection:'row',alignItems:'center',backgroundColor:T.primary,paddingHorizontal:18,paddingVertical:10,borderRadius:10},
  nextBtnT:{fontSize:14,fontWeight:'700',color:T.white,marginRight:2},
  modalHdr:{flexDirection:'row',alignItems:'center',justifyContent:'space-between',padding:16,borderBottomWidth:1,borderBottomColor:T.border},
  submitBtn:{flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:T.success,paddingVertical:14,borderRadius:12,gap:8},
  submitBtnT:{fontSize:15,fontWeight:'700',color:T.white},
  editBtn:{flexDirection:'row',alignItems:'center',justifyContent:'center',paddingVertical:12,borderRadius:12,borderWidth:1,borderColor:T.border},
  editBtnT:{fontSize:14,fontWeight:'600',color:T.primary},
});
